/* ═══════════════════════════════════════════════════════════════════════════
   Agent-run demo — COPY ONLY. No timing lives here (see beats.ts).
   The markup renders the final "done" frame from this data; the timeline
   rewinds it to the "prompt" state and plays it forward.
   ═══════════════════════════════════════════════════════════════════════════ */

export type ModelId = "capuchin" | "claude";

export interface CodeToken {
  text: string;
  color: string;
}

export interface CodeLine {
  tokens: CodeToken[];
  /** Line is new in this run: hidden before the diff beat, streamed in. */
  added?: boolean;
  /** Tokens shown only in the "before" file (e.g. the `];` that closed `= [` on one line). */
  beforeSuffix?: CodeToken[];
}

const KW = "#A855F7";
const PUNCT = "#94A3B8";
const TYPE = "#60A5FA";
const NAME = "#FBBF24";
const VAR = "#F97316";
const STR = "#10B981";

export const AGENT_RUN = {
  srDescription:
    "Animated demo: an agent asked to add CSRF exceptions for webhook and Stripe routes plans four steps. Capuchin completes the first three for $0.00, the one complex step routes to Claude for $0.06, the file is edited and 12 tests pass.",

  appName: "MonkeysCode",

  models: {
    capuchin: { label: "Capuchin", color: "var(--mc-teal)" },
    claude: { label: "Claude", color: "var(--warning)" },
  } satisfies Record<ModelId, { label: string; color: string }>,

  cost: { label: "Run cost", start: 0, end: 0.06 },

  prompt: "Add CSRF $except support for webhook and Stripe routes",

  plan: {
    title: "Plan",
    steps: [
      { text: "Open the CSRF middleware" },
      { text: "Find webhook and Stripe routes" },
      { text: "Add both paths to $except" },
      { text: "Write shouldSkip() and tests", complex: true },
    ],
    complexTag: "complex",
    routedTo: "Claude",
  },

  status: {
    prompt: "Agent ready",
    plan: "Planning",
    capuchin: "Capuchin working",
    handoff: "Routing step 4 to Claude",
    diff: "Editing VerifyCsrfToken.php",
    done: "✓ 12 tests passed",
  },

  statusMeta: ["PHP 8.4", "UTF-8"],

  composerPlaceholder: "Ask the agent…",

  file: {
    name: "VerifyCsrfToken.php",
    secondaryTab: "middleware.mlc",
    lines: [
      { tokens: [{ text: "<?php", color: KW }] },
      { tokens: [] },
      { tokens: [{ text: "namespace ", color: KW }, { text: "App\\Http\\Middleware;", color: "#E2E8F0" }] },
      { tokens: [] },
      { tokens: [{ text: "use ", color: KW }, { text: "MonkeysLegion\\Http\\Middleware\\", color: PUNCT }, { text: "VerifyCsrf;", color: TYPE }] },
      { tokens: [] },
      { tokens: [{ text: "class ", color: KW }, { text: "VerifyCsrfToken ", color: NAME }, { text: "extends ", color: KW }, { text: "VerifyCsrf", color: TYPE }] },
      { tokens: [{ text: "{", color: PUNCT }] },
      {
        tokens: [{ text: "    protected array ", color: KW }, { text: "$except", color: VAR }, { text: " = [", color: PUNCT }],
        beforeSuffix: [{ text: "];", color: PUNCT }],
      },
      { tokens: [{ text: "        " , color: "" }, { text: "'/api/webhook'", color: STR }, { text: ",", color: PUNCT }], added: true },
      { tokens: [{ text: "        ", color: "" }, { text: "'/api/stripe/*'", color: STR }, { text: ",", color: PUNCT }], added: true },
      { tokens: [{ text: "    ];", color: PUNCT }], added: true },
      { tokens: [], added: true },
      { tokens: [{ text: "    public function ", color: KW }, { text: "shouldSkip", color: NAME }, { text: "(", color: PUNCT }, { text: "$request", color: VAR }, { text: "): bool", color: KW }], added: true },
      { tokens: [{ text: "    {", color: PUNCT }], added: true },
      { tokens: [{ text: "        return ", color: KW }, { text: "$this", color: VAR }, { text: "->", color: PUNCT }, { text: "inExceptArray", color: TYPE }, { text: "(", color: PUNCT }, { text: "$request", color: VAR }, { text: ");", color: PUNCT }], added: true },
      { tokens: [{ text: "    }", color: PUNCT }], added: true },
      { tokens: [{ text: "}", color: PUNCT }] },
    ] satisfies CodeLine[] as CodeLine[],
  },

  tree: [
    { name: "monkeyscode-web", kind: "root", depth: 0 },
    { name: "src", kind: "dir", depth: 1, open: true },
    { name: "controllers", kind: "dir", depth: 2, open: true },
    { name: "UserController.php", kind: "file", depth: 3 },
    { name: "VerifyCsrfToken.php", kind: "file", depth: 3, active: true },
    { name: "models", kind: "dir", depth: 2 },
    { name: "middleware.mlc", kind: "file", depth: 2 },
    { name: "routes", kind: "dir", depth: 2 },
    { name: "tests", kind: "dir", depth: 1 },
    { name: "composer.json", kind: "file", depth: 1 },
  ],
  treeTitle: "Explorer",
} as const;

/** Fixed editor line height in px. Diff streaming translates by multiples of this. */
export const EDITOR_LINE_PX = 20;
